import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DropdownButton, Dropdown } from "react-bootstrap";
import { RotateLoader } from "react-spinners";
import { MdOutlineArrowDropDownCircle } from "react-icons/md";
import { GrLinkNext, GrLinkPrevious } from "react-icons/gr";

import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemsManualOrderShipStep1 from "../../components/orders/order-list-item-manual-order-ship-step-1";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";

const TRANSPORTER_CODES = [
  { transporterCode: "POSTNL_3085", displayName: "PostNL Pakket Standaard" },
  { transporterCode: "POSTNL_2928", displayName: "PostNL Brievenbuspakje" },
  { transporterCode: "TNT_BRIEF", displayName: "PostNL Briefpost" },
];

const OrdersManualAdded = () => {
  const auth = useContext(AuthContext); // niet gebruikt hier, maar laten staan voor parity
  const navigate = useNavigate();

  const [loadingState, setLoadingState] = useState("");
  const [checkedOrders, setCheckedOrders] = useState([]); // array
  const [orderActionsAvailable, setOrderActionsAvailable] = useState(false);
  const [shippingMethodBulk, setShippingMethodBulk] = useState(null); // string of speciale value
  const [newShippingMethodBulk, setNewShippingMethodBulk] = useState(null); // object {transporterCode, displayName} of bol-label optie
  const [transporterCodesBolLabels, setTransporterCodesBolLabels] = useState([]); // array opties voor BOL labels

  // Afgeleide: dropdown disabled?
  const bulkDisabled = useMemo(
    () => !orderActionsAvailable || checkedOrders.length === 0,
    [orderActionsAvailable, checkedOrders.length]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingState("Verzendoverzicht wordt geladen");

      try {
        const checkedOrdersProp = JSON.parse(sessionStorage.getItem("checkedOrders") || "[]");
        const shippingMethodProp = JSON.parse(sessionStorage.getItem("shippingMethod") || "null");

        if (!cancelled) {
          setCheckedOrders(Array.isArray(checkedOrdersProp) ? checkedOrdersProp : []);
          setShippingMethodBulk(shippingMethodProp ?? null);

          // Alleen acties beschikbaar als er selectie + methode is
          if (checkedOrdersProp?.length > 0 && shippingMethodProp) {
            setOrderActionsAvailable(true);
          } else {
            setOrderActionsAvailable(false);
          }
        }
      } finally {
        if (!cancelled) setLoadingState("");
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Wanneer shippingMethodBulk of checkedOrders verandert, update evt. BOL-labelopties en initialiseer per order de frontEndChosenDeliveryOption
  useEffect(() => {
    if (!shippingMethodBulk || checkedOrders.length === 0) return;

    const orders = [...checkedOrders];

    // BOL-SHIPPING-LABEL: bouw unieke opties (transporterCode/displayName/offerId)
    if (shippingMethodBulk === "BOL-SHIPPING-LABEL") {
      const optsSet = new Map(); // key op shippingLabelOfferId + transporterCode
      orders.forEach((order) => {
        (order.deliveryOptions || []).forEach((dO) => {
          (dO.options || []).forEach((option) => {
            const key = `${option.shippingLabelOfferId || ""}|${option.transporterCode}`;
            if (!optsSet.has(key)) {
              optsSet.set(key, {
                transporterCode: option.transporterCode,
                displayName: option.labelDisplayName || option.transporterCode,
                shippingLabelOfferId: option.shippingLabelOfferId,
              });
            }
          });
        });
      });
      setTransporterCodesBolLabels(Array.from(optsSet.values()));
    } else {
      setTransporterCodesBolLabels([]);
    }

    // Init frontEndChosenDeliveryOption per order (behoud eerder gekozen items)
    orders.forEach((order) => {
      if (!order.frontEndChosenDeliveryOption) {
        if (shippingMethodBulk === "BOL-SHIPPING-LABEL") {
          // nog geen keuze: placeholder, items overnemen
          const selectedItems = (order.orderItems || []).filter((i) => i.checked);
          order.frontEndChosenDeliveryOption = {
            transporterCode: "", // wordt gekozen via bol label dropdown
            displayName: "Kies verzendmethode",
            orderItems: selectedItems,
          };
        } else {
          // Standaard transporter uit lijst
          const t = TRANSPORTER_CODES.find((tc) => tc.transporterCode === shippingMethodBulk);
          const selectedItems = (order.orderItems || []).filter((i) => i.checked);
          order.frontEndChosenDeliveryOption = {
            transporterCode: shippingMethodBulk,
            displayName: t ? t.displayName : shippingMethodBulk,
            orderItems: selectedItems,
          };
        }
      } else {
        // Zorg dat geselecteerde items niet kwijtraken
        if (!Array.isArray(order.frontEndChosenDeliveryOption.orderItems)) {
          order.frontEndChosenDeliveryOption.orderItems = (order.orderItems || []).filter((i) => i.checked);
        }
      }
    });

    setCheckedOrders(orders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingMethodBulk]);

  const bulkShippingMethodHandler = (eventKey) => {
    if (!eventKey) return;
    const method = JSON.parse(eventKey); // kan TRANSPORTER_CODES item zijn of BOL label optie

    const updated = checkedOrders.map((order) => {
      const current = order.frontEndChosenDeliveryOption || { orderItems: [] };

      // BOL label optie (heeft shippingLabelOfferId)
      if (method.shippingLabelOfferId) {
        // zoek bij order de exacte option
        let chosen = null;
        (order.deliveryOptions || []).forEach((dO) => {
          (dO.options || []).forEach((opt) => {
            if (opt.shippingLabelOfferId === method.shippingLabelOfferId) {
              chosen = opt;
            }
          });
        });

        if (chosen) {
          return {
            ...order,
            frontEndChosenDeliveryOption: {
              ...chosen,
              displayName: method.displayName,
              orderItems: current.orderItems, // behoud selectie
            },
          };
        }
        // fallback – als chosen niet gevonden is, houd vorige staat
        return order;
      }

      // Standaard transporter code
      return {
        ...order,
        frontEndChosenDeliveryOption: {
          ...current,
          transporterCode: method.transporterCode,
          displayName: method.displayName,
          orderItems: current.orderItems,
        },
      };
    });

    setCheckedOrders(updated);
    setNewShippingMethodBulk(method);
  };

  const shippingMethodHandler = (method, index) => {
    const updated = [...checkedOrders];
    const current = updated[index]?.frontEndChosenDeliveryOption || { orderItems: [] };
    updated[index].frontEndChosenDeliveryOption = {
      ...current,
      transporterCode: method.transporterCode,
      displayName: method.displayName,
    };
    setCheckedOrders(updated);
  };

  const confirmToStep2Handler = () => {
    // Validatie: alle orders moeten minstens 1 orderregel hebben
    const invalid = checkedOrders.some(
      (o) => !o.frontEndChosenDeliveryOption?.orderItems?.length
    );
    if (invalid) {
      // optioneel snackbar/toast
      return;
    }
    setLoadingState("Bezig met verzenden van orders");
    sessionStorage.setItem("checkedOrders", JSON.stringify(checkedOrders));
    navigate("/orders/manual-added/ship/step-2");
    window.scrollTo(0, 0);
  };

  const handleRemoveOrderFromChecked = (order, orderItem) => {
    const newArr = [...checkedOrders];
    const id = String(order.id || order.orderId || order._id);
    const i = newArr.findIndex((o) => String(o.id || o.orderId || o._id) === id);

    if (i !== -1) {
      const j = newArr[i].orderItems.findIndex(
        (oItem) => String(oItem?.product?._id) === String(orderItem?.product?._id)
      );

      if (newArr[i].orderItems.length <= 1) {
        newArr.splice(i, 1);
      } else {
        if (j !== -1) newArr[i].orderItems[j].checked = false;

        const allUnchecked = newArr[i].orderItems.every((item) => item.checked === false);
        if (allUnchecked) newArr.splice(i, 1);
      }
      setCheckedOrders(newArr);
      sessionStorage.setItem("checkedOrders", JSON.stringify(newArr));
    }
  };

  const bulkTitle = (() => {
    if (newShippingMethodBulk?.displayName) return (
      <>
        {newShippingMethodBulk.displayName} <MdOutlineArrowDropDownCircle />
      </>
    );

    if (!shippingMethodBulk) {
      return <>Kies verzendmethode <MdOutlineArrowDropDownCircle /></>;
    }

    if (shippingMethodBulk === "ALTERNATE-OPTION" || shippingMethodBulk === "BOL-SHIPPING-LABEL") {
      return <>Kies verzendmethode <MdOutlineArrowDropDownCircle /></>;
    }

    const t = TRANSPORTER_CODES.find((tc) => tc.transporterCode === shippingMethodBulk);
    return <>
      {(t && t.displayName) || shippingMethodBulk} <MdOutlineArrowDropDownCircle />
    </>;
  })();

  return (
    <React.Fragment>
      <div className='title-header title-header-ship-orders'>
        <span>Verzenden van bestellingen</span>
        <div className='shipmentSteps'>
          <div className='shipmentOneStep shipmentFirstStep finished'>
            <div className='shipmentOneStepCircle'>1</div>
            <div className=''>Verzendmethode</div>
          </div>
          <div className='shipmentStepsLine'></div>
          <div className='shipmentOneStep notActive'>
            <div className='shipmentOneStepCircle'>2</div>
            <div className=''>Overzicht</div>
          </div>
          <div className='shipmentStepsLine'></div>
          <div className='shipmentOneStep notActive'>
            <div className='shipmentOneStepCircle'>3</div>
            <div className=''>Bevestiging</div>
          </div>
        </div>
      </div>

      <OrdersHeader recountOrders={false} />

      {checkedOrders.length > 0 && (
        <div className='orders-container-header ship-orders-container-header'>
          <div className='orders-container-header-actions ship-orders-container-header-actions step-1'>
            <div>Bestellingen: {checkedOrders.length}</div>
            <DropdownButton
              align='end'
              id='dropdown-menu-align-end'
              className={bulkDisabled ? "disable" : ""}
              title={bulkTitle}
              disabled={bulkDisabled}
              onSelect={bulkShippingMethodHandler}
            >
              {shippingMethodBulk !== "BOL-SHIPPING-LABEL" &&
                TRANSPORTER_CODES.map((tc) => (
                  <Dropdown.Item key={tc.transporterCode} eventKey={JSON.stringify(tc)}>
                    {tc.displayName}
                  </Dropdown.Item>
                ))}

              {shippingMethodBulk === "BOL-SHIPPING-LABEL" &&
                transporterCodesBolLabels.map((tc) => (
                  <Dropdown.Item
                    key={`${tc.shippingLabelOfferId || "nooffer"}|${tc.transporterCode}`}
                    eventKey={JSON.stringify(tc)}
                  >
                    {tc.displayName}
                  </Dropdown.Item>
                ))}
            </DropdownButton>
          </div>
        </div>
      )}

      {checkedOrders.length > 0 &&
        checkedOrders.map((order, index) => {
          const key = order.id || order.orderId || order._id || index;
          return (
            <OrderListItemsManualOrderShipStep1
              order={order}
              key={key}
              index={index}
              transporterCodes={TRANSPORTER_CODES}
              shippingMethodBulk={shippingMethodBulk}
              handleShippingMethodChange={shippingMethodHandler}
              handleRemoveOrderFromChecked={handleRemoveOrderFromChecked}
            />
          );
        })}

      <div className='orders-container-footer-actions ship-orders-container-footer-actions'>
        <Button
          className='previousBtn'
          onClick={() => {
            navigate("/orders/manual-added");
            window.scrollTo(0, 0);
          }}
        >
          <GrLinkPrevious /> Terug naar bestellingen
        </Button>
        <Button
          className='nextBtn'
          onClick={confirmToStep2Handler}
          disabled={checkedOrders.length === 0}
        >
          Volgende stap <GrLinkNext />
        </Button>
      </div>

      {loadingState && (
        <div className='loading-container'>
          <div className='loading-container-title'>{loadingState}</div>
          <RotateLoader size={100} width={100} height={10} color={"#fff"} loading={!!loadingState} />
        </div>
      )}

      <AssistantToggleButton />
    </React.Fragment>
  );
};

export default OrdersManualAdded;
